// The RFI sheet you send. Same shape as the change-order PDF — same letterhead, the
// same "To" block built from who the job's correspondence goes to — so the two read
// as papers from the same company rather than two different apps.
//
// Opened as a print window rather than built server-side, exactly like the change
// order: no new dependency, and what you see is what prints.

const esc = s => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const para = s => esc(s).replace(/\n/g, '<br/>');
const longDate = s => (s
  ? new Date(String(s).length <= 10 ? s + 'T12:00:00' : s).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  : '—');

async function logoDataUrl() {
  try {
    const res = await fetch(`${window.location.origin}/titan-logo.jpg`);
    const blob = await res.blob();
    return await new Promise(r => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.onerror = () => r(null); fr.readAsDataURL(blob); });
  } catch { return null; }
}

export async function generateRfiPDF(rfi, project, bid) {
  const win = window.open('', '_blank');            // opened on the click gesture
  const logo = (await logoDataUrl()) || `${window.location.origin}/titan-logo.jpg`;
  const no = `RFI-${String(rfi.rfi_number || 0).padStart(2, '0')}`;

  // Who it goes to: the contractor that awarded us the work, or the owner when we're
  // the prime. The same rule the change order and the job banner use.
  const company = project?.awarded_by_company || project?.awarded_by_name || bid?.client_name || 'Owner / General Contractor';
  const attn = rfi.asked_to || (project?.awarded_by_company && project?.awarded_by_name !== project?.awarded_by_company ? project?.awarded_by_name : '');
  const addr = [project?.awarded_by_address1, project?.awarded_by_address2].filter(Boolean);

  const impacts = [
    rfi.cost_impact ? 'Cost' : null,
    rfi.schedule_impact ? 'Schedule' : null,
  ].filter(Boolean);

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(no)} — ${esc(project?.project_number || '')}</title>
  <style>
    @page { size: letter; margin: 0.5in; }
    body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; }
    .avoid { break-inside: avoid; page-break-inside: avoid; }
    .box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; }
    .lbl { font-size: 10.5px; text-transform: uppercase; letter-spacing: .5px; color: #6b7280; margin-bottom: 4px; }
  </style></head><body onload="window.print()">
  <div style="padding:6px 2px;"><div id="fit">

    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1e3a5f;padding-bottom:12px;">
      <div><img src="${logo}" alt="Titan" style="height:52px;"/></div>
      <div style="text-align:right;">
        <div style="font-size:23px;font-weight:800;color:#1e3a5f;letter-spacing:1px;">REQUEST FOR INFORMATION</div>
        <div style="font-size:15px;font-weight:700;margin-top:2px;">${esc(no)}</div>
      </div>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:16px;">
      <div class="box">
        <div class="lbl">To</div>
        <div style="font-size:13.5px;font-weight:700;">${esc(company)}</div>
        ${attn ? `<div style="font-size:12.5px;">Attn: ${esc(attn)}</div>` : ''}
        ${addr.map(a => `<div style="font-size:12.5px;color:#374151;">${esc(a)}</div>`).join('')}
      </div>
      <div class="box">
        <div class="lbl">Job</div>
        <div style="font-size:13.5px;font-weight:700;">${esc(project?.project_number || '')} — ${esc(project?.name || bid?.job_name || '')}</div>
        ${bid?.job_location ? `<div style="font-size:12px;color:#374151;">${esc(bid.job_location)}</div>` : ''}
        ${bid?.client_name && project?.awarded_by_company ? `<div style="font-size:11.5px;color:#9ca3af;margin-top:4px;">Owner: ${esc(bid.client_name)}</div>` : ''}
      </div>
    </div>

    <div style="display:flex;gap:22px;margin-top:14px;font-size:12.5px;">
      <div><span style="color:#6b7280;">Date asked:</span> <b>${longDate(rfi.asked_at)}</b></div>
      <div><span style="color:#6b7280;">Answer needed by:</span> <b>${rfi.needed_by ? longDate(rfi.needed_by) : '—'}</b></div>
      ${rfi.reference ? `<div><span style="color:#6b7280;">Reference:</span> <b>${esc(rfi.reference)}</b></div>` : ''}
    </div>

    ${rfi.title ? `<div style="margin-top:16px;font-size:16px;font-weight:800;">${esc(rfi.title)}</div>` : ''}

    <div class="avoid" style="margin-top:12px;">
      <div style="background:#1e3a5f;color:#fff;padding:7px 12px;border-radius:8px 8px 0 0;font-size:12px;font-weight:700;letter-spacing:.5px;">QUESTION</div>
      <div style="border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;padding:12px 14px;font-size:13px;line-height:1.55;min-height:70px;">
        ${para(rfi.question) || '<span style="color:#9ca3af;">—</span>'}
      </div>
    </div>

    <div class="avoid" style="margin-top:14px;">
      <div style="background:${rfi.answer ? '#0f766e' : '#6b7280'};color:#fff;padding:7px 12px;border-radius:8px 8px 0 0;font-size:12px;font-weight:700;letter-spacing:.5px;">
        ANSWER${rfi.answered_at ? ` &nbsp;·&nbsp; ${longDate(rfi.answered_at)}` : ''}
      </div>
      <div style="border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;padding:12px 14px;font-size:13px;line-height:1.55;min-height:110px;">
        ${rfi.answer ? para(rfi.answer) : '<span style="color:#9ca3af;">Awaiting response.</span>'}
      </div>
    </div>

    ${impacts.length ? `<div class="avoid" style="margin-top:12px;padding:9px 12px;border:1px solid #fde68a;background:#fffbeb;border-radius:8px;font-size:12.5px;color:#92400e;">
      <b>Note:</b> the answer to this RFI affects ${impacts.join(' and ').toLowerCase()}. A change order may follow.
    </div>` : ''}

    <div class="tail avoid" style="padding-top:34px;display:grid;grid-template-columns:1fr 1fr;gap:40px;">
      <div><div style="border-top:1px solid #374151;padding-top:6px;font-size:11px;color:#6b7280;">Asked by – Titan Electrical Contracting, Inc.</div></div>
      <div><div style="border-top:1px solid #374151;padding-top:6px;font-size:11px;color:#6b7280;">Answered by – ${esc(company)}</div></div>
    </div>

    <div class="avoid" style="margin-top:22px;padding-top:12px;border-top:3px solid #1e3a5f;font-size:12px;color:#6b7280;line-height:1.5;">
      <div style="font-size:14px;font-weight:700;color:#1e3a5f;">TITAN ELECTRICAL CONTRACTING, INC.</div>
      <div>Suite 100, 5450 Mill Street, Reno, NV 89502 &nbsp;·&nbsp; (775) 857-4500</div>
      <div>NV Licenses · Class A #90896 · Class B #69843 · Class C #69814</div>
    </div>
  </div></div>
  </body></html>`;

  win.document.write(html);
  win.document.close();
}
